import { useState, useEffect, useRef } from "react";
import { isInIframe } from "../utils/authSession";

const ALLOWED_PARENT_ORIGINS = (process.env.REACT_APP_CMS_PAGE_URL || "")
  .split(",")
  .map((url) => url.trim())
  .filter(Boolean);

const IFRAME_AUTH_TIMEOUT_MS = 10000;

/**
 * Hook that handles iframe postMessage authentication.
 * When embedded in an iframe, listens for PRZ_SESSION_INIT from the parent CMS.
 * Falls back to normal login flow after a timeout.
 *
 * @param {function} onTokenReceived - Called with the token when received from parent
 * @param {boolean} enabled - Whether the iframe handshake should be active
 * @returns {{ isIframe: boolean, waitingForIframeAuth: boolean }}
 */
const useIframeAuth = (onTokenReceived, enabled = true) => {
  const isIframe = isInIframe();
  const [waitingForIframeAuth, setWaitingForIframeAuth] = useState(
    isIframe && enabled
  );
  const iframeTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isIframe || !enabled) {
      setWaitingForIframeAuth(false);
      return;
    }

    setWaitingForIframeAuth(true);

    const handleMessage = (event) => {
      if (event.source !== window.parent) return;
      if (!ALLOWED_PARENT_ORIGINS.includes(event.origin)) return;
      if (event.data?.type !== "PRZ_SESSION_INIT") return;

      const token = event.data.token;
      if (!token) return;

      localStorage.setItem("authToken", token);
      localStorage.removeItem("refreshToken");
      localStorage.setItem("loginTimestamp", String(Date.now()));
      onTokenReceived(token);
      setWaitingForIframeAuth(false);

      if (iframeTimeoutRef.current) {
        clearTimeout(iframeTimeoutRef.current);
        iframeTimeoutRef.current = null;
      }
    };

    window.addEventListener("message", handleMessage);

    window.parent.postMessage({ type: "PRZ_FRAME_READY" }, "*");

    iframeTimeoutRef.current = setTimeout(() => {
      setWaitingForIframeAuth(false);
    }, IFRAME_AUTH_TIMEOUT_MS);

    return () => {
      window.removeEventListener("message", handleMessage);
      if (iframeTimeoutRef.current) {
        clearTimeout(iframeTimeoutRef.current);
      }
    };
  }, [enabled, isIframe, onTokenReceived]);

  return { isIframe, waitingForIframeAuth };
};

export default useIframeAuth;
